import React, { useRef } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import styled from 'styled-components';
import UpdateSectionForm from './UpdateSectionForm';

const SectionRow = styled.div<{ isDragging: boolean }>`
    ${({ theme, isDragging }) => `
        margin-bottom: ${theme.spacing(2)}px;
        opacity: ${isDragging ? 0 : 1};
    `}
    &:hover {
        cursor: grab;
    }
`;

const SECTION = 'SECTION';

interface Props {
    name: string;
    count: number;
    id: string;
    index: number;
    setShouldCommit: React.Dispatch<React.SetStateAction<boolean>>;
    moveSection: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

const DraggableUpdateSectionForm: React.FC<Props> = ({ id, name, count, index, setShouldCommit, moveSection }) => {
    const ref = useRef<HTMLDivElement>(null);
    
    const [, drop] = useDrop({
        accept: SECTION,
        hover(item: DragItem, monitor: DropTargetMonitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            moveSection(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
        drop(){
            setShouldCommit(true);
        }
    });

    const [{ isDragging }, drag] = useDrag({
        item: { type: SECTION, id, index },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(drop(ref));
    return (
        <SectionRow ref={ref} isDragging={isDragging}>
            <UpdateSectionForm id={id} name={name} count={count} />
        </SectionRow>
    );
};

export default DraggableUpdateSectionForm;